import {
  Autocomplete,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useToast from "../../../../../../hooks/useToast";
import useConfirm from "../../../../../../hooks/useConfirm";
import { TYPE_NOTIFICATION } from "../../../../shares/NotificationBull";
import AppButton from "../../../../shares/AppButton";
import { SQCQuestionService } from "../../../../../../service/SQC_form/SQCQuestionService";
import { TYPE_REPONSE } from "../../../../offre/VolontariatPostuleForm";
import StandardSkeletoon from "../../../../shares/loader/StandardSkeletoon";

const INIT_QUESTIONNAIRE_INFO = {
  id_sqc_form: "0",
  question: "",
  qs_requis: "1",
  pj_requis: "0",
  allow_comment: "0",
  comment_requis: "0",
  type_reponse: "1",
};

const INIT_FORM_ERROR = {
  id_sqc_form_error: null,
  question_error: null,
  qs_requis_error: null,
  pj_requis_error: null,
  comment_requis_error: null,
  type_reponse_error: null,
};

const SQCQuestionDialogForm = ({
  open,
  onFormClose,
  onFormOpen,
  idQuestion,
  idForm,
}) => {
  const [questionnaireInfo, setQuestionnaireInfo] = useState(
    INIT_QUESTIONNAIRE_INFO
  );
  const [formErrors, setFormErrors] = useState(INIT_FORM_ERROR);
  const [submited, setSubmited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { Toast, showToast } = useToast();
  const navigate = useNavigate();
  const { DialogComponent, openConfirmDialog } = useConfirm();

  const handleFormClose = () => {
    onFormClose();
    setQuestionnaireInfo(INIT_QUESTIONNAIRE_INFO);
  };

  const handleChangeFormInfo = (e) => {
    const { name, value } = e.target;
    setQuestionnaireInfo({ ...questionnaireInfo, [name]: value });
  };

  const handleFetchQuestionInfo = () => {
    setFormLoading(true);
    SQCQuestionService.findOneById(idQuestion)
      .then((res) => {
        if (res && res.data) {
          setQuestionnaireInfo(res.data);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckDataValidation = () => {
    setFormErrors({
      id_sqc_form_error: questionnaireInfo.id_sqc_form
        ? null
        : "Identifiant du formulaire est obligatoire",
      question_error: questionnaireInfo.question
        ? null
        : "La question est obligatoire",
      qs_requis_error: questionnaireInfo.qs_requis
        ? null
        : "La question est requis ou non?",
      pj_requis_error: questionnaireInfo.pj_requis
        ? questionnaireInfo.pj_requis === "1" &&
          questionnaireInfo.qs_requis === "0"
          ? "Une piece jointe est requise uniquement si la question est obligatoire"
          : null
        : "Une piece joint est il requis ou non?",
      allow_comment_error: questionnaireInfo.allow_comment
        ? null
        : "Mentionner si le commentaire est autorisé ou non",
      comment_requis_error: questionnaireInfo.allow_comment
        ? questionnaireInfo.allow_comment === "0" &&
          questionnaireInfo.comment_requis === "1"
          ? "Commentaire est obligatoire si c'est autorisé à commenter"
          : null
        : "Un commentaire est il obligatoire ou non?",
      type_reponse_error:
        !questionnaireInfo.type_reponse ||
        questionnaireInfo.type_reponse !== "0"
          ? null
          : "Le type de la reponse attendu est requis",
    });
    return Object.values(formErrors).every((error) => error === null);
  };

  const handleSave = () => {
    if (idQuestion) {
      handleUpdate();
    } else {
      handleInsert();
    }
  };
  const handleInsert = () => {
    setSubmited(true);
    setLoading(true);
    SQCQuestionService.insert(questionnaireInfo)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    setSubmited(true);
    setLoading(true);
    SQCQuestionService.update(questionnaireInfo)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    setSubmited(true);
    if (handleCheckDataValidation()) {
      handleSave();
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Veuillez verifier les champs");
    }
  };

  useEffect(() => {
    if (questionnaireInfo) {
      handleCheckDataValidation();
    }
  }, [questionnaireInfo]);

  useEffect(() => {
    setQuestionnaireInfo({ ...questionnaireInfo, id_sqc_form: idForm });
    if (idForm && open) {
      handleFetchQuestionInfo();
    }
  }, [idForm, open]);

  return (
    <>
      <DialogComponent />
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="xs">
        <Toast />
        <Grid>
          <StandardSkeletoon escalier={true} m={4} row={4} show={formLoading} />
        </Grid>
        <Paper
          sx={{ m: 3, display: formLoading ? "none" : "block" }}
          elevation={0}
        >
          <Grid>
            <CardHeader
              title={
                idQuestion ? "Modifier questionnaire" : "Creer questionnaire"
              }
            />
            <CardContent>
              <Grid container>
                <FormControl fullWidth>
                  <label>Question à poser</label>
                  <TextField
                    multiline
                    rows={3}
                    id="question"
                    name="question"
                    onChange={handleChangeFormInfo}
                    size="small"
                    value={questionnaireInfo.question || ""}
                    error={formErrors.question_error && submited}
                    helperText={submited ? formErrors.question_error : ""}
                    placeholder="Inserer ici la question à poser"
                    fullWidth
                    aria-describedby="question_helper"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Type de reponse</label>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    size="small"
                    id="type_reponse"
                    name="type_reponse"
                    options={TYPE_REPONSE}
                    value={TYPE_REPONSE.find(
                      (item) => item.id === questionnaireInfo.type_reponse
                    )}
                    onChange={(event, newValue) => {
                      handleChangeFormInfo({
                        target: {
                          name: "type_reponse",
                          value: newValue ? newValue.id : "0",
                        },
                      });
                    }}
                    getOptionLabel={(option) => option.reponse_name ?? ""}
                    sx={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={formErrors.type_reponse_error && submited}
                        value={
                          TYPE_REPONSE.find(
                            (item) => item.id === questionnaireInfo.type_reponse
                          )?.reponse_name || ""
                        }
                        helperText={
                          submited ? formErrors.type_reponse_error : ""
                        }
                        label="Type de reponse"
                      />
                    )}
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  error={formErrors.qs_requis_error && submited}
                  component="fieldset"
                  variant="standard"
                >
                  <label>Reponse obligatoire</label>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={questionnaireInfo.qs_requis === "1"}
                        onChange={(e) =>
                          handleChangeFormInfo({
                            target: {
                              name: "qs_requis",
                              value: e.target.checked ? "1" : "0",
                            },
                          })
                        }
                        name="qs_requis"
                      />
                    }
                    label={questionnaireInfo.qs_requis === "0" ? "Non" : "Oui"}
                  />
                  {formErrors.qs_requis_error && submited && (
                    <FormHelperText>
                      {formErrors.qs_requis_error}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  fullWidth
                  error={formErrors.pj_requis_error && submited}
                  component="fieldset"
                  variant="standard"
                >
                  <label>Piece jointe requise</label>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={questionnaireInfo.pj_requis === "1"}
                        onChange={(e) =>
                          handleChangeFormInfo({
                            target: {
                              name: "pj_requis",
                              value: e.target.checked ? "1" : "0",
                            },
                          })
                        }
                        name="pj_requis"
                      />
                    }
                    label={questionnaireInfo.pj_requis === "0" ? "Non" : "Oui"}
                  />
                  {formErrors.pj_requis_error && submited && (
                    <FormHelperText>
                      {formErrors.pj_requis_error}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  fullWidth
                  error={formErrors.pj_requis_error && submited}
                  component="fieldset"
                  variant="standard"
                >
                  <label>Commentaire autorisé</label>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={questionnaireInfo.allow_comment === "1"}
                        onChange={(e) =>
                          handleChangeFormInfo({
                            target: {
                              name: "allow_comment",
                              value: e.target.checked ? "1" : "0",
                            },
                          })
                        }
                        name="allow_comment"
                      />
                    }
                    label={
                      questionnaireInfo.allow_comment === "0" ? "Non" : "Oui"
                    }
                  />
                  {formErrors.allow_comment_error && submited && (
                    <FormHelperText>
                      {formErrors.allow_comment_error}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  fullWidth
                  error={formErrors.comment_requis_error && submited}
                  component="fieldset"
                  variant="standard"
                >
                  <label>Commentaire requis</label>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={questionnaireInfo.comment_requis === "1"}
                        onChange={(e) =>
                          handleChangeFormInfo({
                            target: {
                              name: "comment_requis",
                              value: e.target.checked ? "1" : "0",
                            },
                          })
                        }
                        name="comment_requis"
                      />
                    }
                    label={
                      questionnaireInfo.comment_requis === "0" ? "Non" : "Oui"
                    }
                  />
                  {formErrors.comment_requis_error && submited && (
                    <FormHelperText>
                      {formErrors.comment_requis_error}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={loading}
                text={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={(e) => handleSubmit()}
              />
              <AppButton
                text={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={(e) => handleFormClose()}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default SQCQuestionDialogForm;
